// Moves are data (battle design, section 2). Each element has five rows (basic, light, heavy, coverage strike,
// wave); each line and boss has one Signature. Cooldowns are never stored: they come from the one curve,
// cooldownMs(). lineKit() builds a creature's kit from rules, and a line can override any slot in species.ts.
//
// The Wave (an area move) is learned at Lv 60 and fires on the hunt only (server/hunt.ts): it hits the target and
// splashes every standing wild within WAVE_RADIUS tiles at WAVE_SPLASH of the blow. A Rune Tablet (shared/tablets.ts)
// swaps the Coverage slot to another element: same power and cooldown, so a sideways choice.
import { BEATS, ELEMENT_IDS, type Element } from './elements.ts';
import { BOSSES, LINES, SPECIES, type BossDef, type LineDef } from './species.ts';
import { AWAKENINGS, awakenedId } from './awakening-data.ts';

export type Slot = 'basic' | 'light' | 'heavy' | 'coverage' | 'wave' | 'signature' | 'awakened';
export type AnimKind = 'jab' | 'slash' | 'projectile' | 'beam' | 'burst' | 'wave' | 'signature';

export interface MoveAnim { kind: AnimKind; fx: string; castMs: number }

export interface MoveDef {
  id: string;
  name: string;
  element: Element;
  power: number;
  slot: Slot;
  /** Hits the target's neighbours too (on the hunt); its cooldown is set as if power WAVE_CD_POWER. */
  wave?: boolean;
  anim: MoveAnim;
}

/**
 * The kit a creature fights with: one move id per slot. The Wave is cast on the hunt only. `awakened`: an Awakened
 * form's own move (shared/awakening.ts), only on those species; KIT_SLOTS leaves it out.
 */
export interface Kit { basic: string; light: string; heavy: string; coverage: string; wave: string; signature: string; awakened?: string }
export type KitSlot = keyof Kit;
export const KIT_SLOTS: KitSlot[] = ['basic', 'light', 'heavy', 'coverage', 'wave', 'signature'];

export const SLOT_POWER: Record<Slot, number> = { basic: 30, light: 55, heavy: 100, coverage: 70, wave: 60, signature: 300, awakened: 600 };
/**
 * The Awakened move's fixed cooldown, off the curve (the curve would give 53 s): the reference's mega move is 600
 * power on half a signature move's cooldown, so ours is twice the Signature's power on about its cooldown (30 s).
 * Speed still shortens it like any move.
 */
export const AWAKENED_CD_MS = 30_000;
/** A wave's cooldown is the curve's at this power. */
export const WAVE_CD_POWER = 90;
/**
 * On the hunt a Wave also hits every standing wild within this many tiles of its target (centre to centre)... One
 * tile, the target's neighbours: at 1.5 the splash pushed far-over-level hunts (3×) about 30 kills/h past the
 * walking limit; at 1 they stay on it (sim/TUNING_LOG.md, phase 8).
 */
export const WAVE_RADIUS = 1;
/** WAVE_RADIUS in words, for the client. */
export const WAVE_REACH_TEXT = WAVE_RADIUS === 1 ? 'one tile' : `${WAVE_RADIUS} tiles`;
/** ...at this share of the blow (the formula with power × WAVE_SPLASH). */
export const WAVE_SPLASH = 0.6;
/** The Wave fires when at least this many wilds (the target included) are in its reach. */
export const WAVE_MIN_TARGETS = 2;

/** The one cooldown curve: 3 s + power/12 s. The basic attack has none (it fires on any beat nothing else takes). */
export function cooldownMs(m: Pick<MoveDef, 'power' | 'slot' | 'wave'>): number {
  if (m.slot === 'basic') return 0;
  if (m.slot === 'awakened') return AWAKENED_CD_MS;
  const p = m.wave ? WAVE_CD_POWER : m.power;
  return Math.round((3 + p / 12) * 1000);
}

/** Level a slot unlocks at (the signature also needs the final stage). */
export const SLOT_LEVEL: Record<KitSlot, number> = { basic: 1, light: 1, heavy: 20, coverage: 40, wave: 60, signature: 1, awakened: 1 };

const ANIM: Record<Slot, { kind: AnimKind; castMs: number }> = {
  basic: { kind: 'jab', castMs: 0 },
  light: { kind: 'slash', castMs: 250 },
  coverage: { kind: 'projectile', castMs: 250 },
  heavy: { kind: 'burst', castMs: 600 },
  wave: { kind: 'wave', castMs: 600 },
  signature: { kind: 'signature', castMs: 1200 },
  awakened: { kind: 'signature', castMs: 1200 },
};

export type ElementMoveNames = Record<Exclude<Slot, 'signature' | 'awakened'>, string>;

/** Per element: basic, light, heavy, coverage strike and wave. A new element needs one row here. */
export const ELEMENT_MOVE_NAMES: Record<Element, ElementMoveNames> = {
  flame: { basic: 'Scorch', light: 'Flame Lash', heavy: 'Forge Blow', coverage: 'Cinder Strike', wave: 'Pyre Wave' },
  tide: { basic: 'Spray', light: 'Undertow', heavy: 'Leviathan Surge', coverage: 'Salt Jet', wave: 'Riptide Ring' },
  grove: { basic: 'Bramble', light: 'Thorn Lash', heavy: 'Root Crush', coverage: 'Seed Shot', wave: 'Spore Burst' },
  storm: { basic: 'Crackle', light: 'Spark Jab', heavy: 'Thunder Maul', coverage: 'Gale Strike', wave: 'Chain Bolt' },
  stone: { basic: 'Grit', light: 'Pebble Volley', heavy: 'Mountain Fall', coverage: 'Stone Sling', wave: 'Quake Ring' },
  frost: { basic: 'Chill', light: 'Rime Bite', heavy: 'Glacier Fall', coverage: 'Frost Dart', wave: 'Hailstorm' },
  shade: { basic: 'Shadow Nip', light: 'Evil Eye', heavy: 'Night Rend', coverage: 'Grave Bolt', wave: 'Gloom Veil' },
  light: { basic: 'Glimmer', light: 'Glint', heavy: 'Sunlance', coverage: 'Radiant Strike', wave: 'Dawn Flare' },
};

/** One Signature per line (final stage) and per boss. Element: the owner's primary. */
export const SIGNATURE_NAMES: Record<string, string> = {
  // Midgard
  wolf: 'Chainbreaker',
  draugr: 'Barrow Reckoning',
  raven: "Hugin's Omen",
  goat: 'Hammer-Peal',
  ember: "Surtr's Brand",
  squirrel: 'Worldtree Dash',
  boar: 'Sunbristle Charge',
  nykk: 'Drowning Gallop',
  troll: 'Bergfall',
  wyrm: 'World-Coil',
  seal: 'Floe Crash',
  tomte: 'Hearthlight',
  valkyrie: 'Chooser of the Slain',
  nidhogg: 'Root-Rend',
  huldra: 'Hollow-Back Lure',
  sleipnir: 'Eight-Hoof Thunder',
  // Hellas
  harpy: 'Snatching Gale',
  satyr: "Pan's Pipes",
  minotaur: 'Labyrinth Rush',
  cerberus: 'Gate of Hades',
  hydra: 'Nine-Neck Lash',
  pegasus: 'Starfall Gallop',
  phoenix: 'Rebirth Pyre',
  chimera: 'Three-Beast Fury',
  siren: 'Drowning Song',
  talos: 'Molten Embrace',
  ram: 'Golden Fleece',
  cyclops: 'Forged Thunderbolt',
  gorgon: 'Stone Gaze',
  nemeanlion: 'Unbroken Hide',
  empusa: 'Bronze-Heel Hunt',
  // Yomi
  kodama: 'Echo of the Grove',
  parasol: 'Hundredth Rain',
  tanuki: 'Thousand Guises',
  kappa: 'Riverpull',
  yurei: 'Willow Lament',
  fox: 'Ninefold Foxfire',
  tengu: 'Feather-Fan Tempest',
  baku: 'Reverie Devour',
  oni: 'Iron Kanabo',
  ryu: 'Heavenly Deluge',
  jorogumo: 'Silken Snare',
  nekomata: 'Twin-Tail Ghostfire',
  gashadokuro: 'Starving Grasp',
  yukionna: 'Breath of the Blizzard',
  raiju: 'Thunderbelly Strike',
  // Round 3
  hare: 'Aurora Leap',
  lynx: "Freyja's Pounce",
  owl: 'Silent Talon',
  elk: 'River-Antler Charge',
  dwarf: 'Forge Strike',
  fossegrim: 'Waterfall Reel',
  jotun: 'Glacier Maul',
  mara: 'Nightmare Weight',
  kraken: 'Abyssal Grip',
  gowl: 'Owl of Wisdom',
  dolphin: 'Cresting Wave',
  crab: 'Crushing Pincer',
  myrmidon: 'Phalanx',
  stymph: 'Bronze Feather Volley',
  nymph: 'Oakbloom',
  griffin: 'Sky-Lord Dive',
  lamia: 'Coil of the Queen',
  scylla: 'Six-Fold Maw',
  lantern: 'Ghostfire Lick',
  komainu: 'Guardian Roar',
  tsuchinoko: 'Rolling Gourd',
  namazu: 'Great Tremor',
  kamaitachi: 'Sickle Wind',
  kirin: 'Holy Thunderhoof',
  rokurokubi: 'Long-Neck Lure',
  nue: 'Night Thunder',
  umibozu: 'Swallowing Wave',
  // Round 4
  otter: 'Ring of Andvari',
  alf: 'Alfheim Radiance',
  bear: 'Berserker Maul',
  garm: 'Gnipa Howl',
  hel: "Helheim's Embrace",
  hraesvelgr: 'Corpse-Wind Gale',
  hippocamp: "Poseidon's Gallop",
  hind: 'Golden Bound',
  bee: 'Honeyed Swarm',
  hippalectryon: 'Dawn Crow',
  centaur: "Chiron's Volley",
  sphinx: 'Riddle of Thebes',
  ladon: 'Hundred-Fang Coil',
  mujina: 'Blank Face',
  momen: 'Cotton Snare',
  koi: 'Dragon Gate Leap',
  usagi: 'Moon Mallet',
  nurikabe: 'Endless Wall',
  shachi: 'Castle Spout',
  amabie: 'Warding Omen',
  ushioni: 'Ox-Demon Rush',
  // Bosses
  fenrir: 'Ragnarök Howl',
  typhon: 'Hundred Heads',
  hachimata: 'Eight-Fold Flood',
};

const row = (id: string, name: string, element: string, slot: Slot): MoveDef => ({
  id, name, element: element as Element, power: SLOT_POWER[slot], slot, ...(slot === 'wave' ? { wave: true } : {}),
  // The Awakened move borrows its element's Signature effect until art draws its own.
  anim: { kind: ANIM[slot].kind, fx: `${element}-${slot === 'awakened' ? 'signature' : slot}`, castMs: ANIM[slot].castMs },
});

/** The five rows of one element. Takes any element id, so an element added as data gets its full set. */
export function elementMoves(element: string, names: ElementMoveNames): MoveDef[] {
  return (['basic', 'light', 'heavy', 'coverage', 'wave'] as const).map((slot) => row(`${element}.${slot}`, names[slot], element, slot));
}

export const signatureId = (owner: string) => `${owner}.signature`;
/** The Awakened move of an Awakened species (its id is `<species>-awakened`). */
export const awakenedMoveId = (awakenedSpecies: string) => `${awakenedSpecies}.awakened`;

/** Every move: 5 per element, then each line's and each boss's Signature. */
export const MOVES: MoveDef[] = [
  ...ELEMENT_IDS.flatMap((el) => elementMoves(el, ELEMENT_MOVE_NAMES[el])),
  ...[...LINES, ...BOSSES].map((o) => row(signatureId(o.id), SIGNATURE_NAMES[o.id], o.elements[0], 'signature')),
  // Awakened moves: 600 power, the awakened form's primary element (so always same-element), 30 s.
  ...AWAKENINGS.map((a) => row(awakenedMoveId(awakenedId(a.from)), a.move, a.elements[0], 'awakened')),
];
export const MOVE: Record<string, MoveDef> = Object.fromEntries(MOVES.map((m) => [m.id, m]));

export const moveId = (element: string, slot: Exclude<Slot, 'signature' | 'awakened'>) => `${element}.${slot}`;

/**
 * Default coverage element: the first element, in chart order, that is not one of the creature's own, hits more than
 * one element hard, hits none of its own elements hard, and is not something its own elements already beat. Falls
 * back to the first element that is not its own. `elements` and `beats` default to the live chart.
 */
export function coverageEl(own: readonly string[], beats: Record<string, readonly string[]> = BEATS, elements: readonly string[] = ELEMENT_IDS): string {
  return elements.find((x) => !own.includes(x) && beats[x].length > 1 && !beats[x].some((y) => own.includes(y)) && !own.some((e) => beats[e].includes(x)))
    ?? elements.find((x) => !own.includes(x))!;
}

/**
 * A line's (or boss's) full kit: basic and light of the primary element, heavy of the second element (else the
 * primary), the coverage strike of `line.coverage ?? coverageEl(elements)`, the primary element's Wave, and its
 * Signature. `line.kit` overrides any slot.
 */
export function lineKit(line: Pick<LineDef, 'id' | 'elements'> & Partial<Pick<LineDef, 'coverage' | 'kit'>>): Kit {
  const [p, s] = line.elements;
  return {
    basic: moveId(p, 'basic'),
    light: moveId(p, 'light'),
    heavy: moveId(s ?? p, 'heavy'),
    coverage: moveId(line.coverage ?? coverageEl(line.elements), 'coverage'),
    wave: moveId(p, 'wave'),
    signature: signatureId(line.id),
    ...line.kit,
  };
}

const OWNER: Record<string, LineDef | BossDef> = Object.fromEntries([...LINES, ...BOSSES].map((o) => [o.id, o]));

/**
 * A Rune Tablet on the kit: the Coverage slot becomes that element's coverage strike (same power, same cooldown).
 * No tablet (or an unknown element) leaves the kit as it is.
 */
export function withTablet<K extends Partial<Kit>>(kit: K, tablet?: string): K {
  return tablet && MOVE[moveId(tablet, 'coverage')] ? { ...kit, coverage: moveId(tablet, 'coverage') } : kit;
}

/** A species' full kit (every slot, learned or not), with the creature's Rune Tablet if it has one. */
export function speciesKit(speciesId: string, tablet?: string): Kit {
  const k = withTablet(lineKit(OWNER[SPECIES[speciesId].line]), tablet);
  // An Awakened form keeps its line's kit and adds its Awakened move.
  return SPECIES[speciesId].awakened ? { ...k, awakened: awakenedMoveId(speciesId) } : k;
}

/**
 * The moves a creature of this species has at this level: heavy from Lv 20, coverage from Lv 40, the Wave from
 * Lv 60, the Signature on its final stage. `tablet`: the element of an applied Rune Tablet (CreatureDTO.tablet).
 */
export function kitAt(speciesId: string, level: number, tablet?: string): Partial<Kit> {
  const sp = SPECIES[speciesId];
  const k = speciesKit(speciesId, tablet);
  const out: Partial<Kit> = {};
  for (const slot of KIT_SLOTS) {
    if (level < SLOT_LEVEL[slot]) continue;
    if (slot === 'signature' && sp.evolvesTo) continue;
    out[slot] = k[slot];
  }
  if (k.awakened) out.awakened = k.awakened;
  return out;
}

const basicCache = new Map<string, string>();
/** The basic attack's move id for a species: what every blow is labelled with until the move kit fights. */
export function basicMoveOf(speciesId: string): string {
  let id = basicCache.get(speciesId);
  if (id === undefined) basicCache.set(speciesId, (id = moveId(SPECIES[speciesId].elements[0], 'basic')));
  return id;
}
